import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import type { VsmCatalog } from "@playbookai/ui";
import { useAuthStore } from "../auth/auth-store.js";
import {
  getVsmCatalogItem,
  listVsmCatalogPage,
} from "../api/vsm-catalog-api.js";

/** Lazy VSM picker catalogue with cursor pagination and server-side search. */
export function useVsmCatalog(): VsmCatalog {
  const config = useApiClient();
  const claims = useAuthStore((state) => state.claims);
  const authed = claims?.tenantId != null && claims?.userId != null;
  const [active, setActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const activate = useCallback((ids: readonly string[]) => {
    setActive(true);
    setSelectedIds((previous) => {
      const next = [...new Set(ids)];
      return previous.length === next.length &&
        previous.every((id, index) => id === next[index])
        ? previous
        : next;
    });
  }, []);
  const query = useInfiniteQuery({
    queryKey: [
      "vsm-catalog",
      claims?.tenantId ?? "none",
      claims?.userId ?? "anon",
      searchQuery.trim(),
    ],
    enabled: active && authed,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listVsmCatalogPage(config, {
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 20,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
    retry: false,
  });
  const selectedQueries = useQueries({
    queries: selectedIds.map((id) => ({
      queryKey: ["vsm-catalog-item", claims?.tenantId ?? "none", id],
      queryFn: () => getVsmCatalogItem(config, id),
      enabled: active && authed,
      staleTime: 60_000,
      retry: false,
    })),
  });
  const selectedItems = selectedQueries.flatMap((item) =>
    item.data ? [item.data] : [],
  );
  const vsms = useMemo(() => {
    const rows = [
      ...selectedItems,
      ...(query.data?.pages.flatMap((page) => page.items) ?? []),
    ];
    return [...new Map(rows.map((row) => [row.id, row])).values()];
  }, [query.data, selectedItems]);
  const selectionError = selectedQueries.some((item) => item.isError);
  return {
    vsms,
    isLoading:
      (query.isPending && query.isFetching) ||
      selectedQueries.some((item) => item.isPending && item.isFetching),
    isLoadingMore: query.isFetchingNextPage,
    error:
      query.error || selectionError
        ? "Value stream choices couldn’t be loaded."
        : null,
    hasMore: query.hasNextPage,
    searchQuery,
    activate,
    onSearchChange: setSearchQuery,
    onLoadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage)
        void query.fetchNextPage();
    },
    onRetry: () => {
      void query.refetch();
      for (const selectedQuery of selectedQueries) {
        if (selectedQuery.isError) void selectedQuery.refetch();
      }
    },
  };
}
